// src/Contexts/AuthWrapperContext.tsx
/* eslint-disable react-refresh/only-export-components -- file exports context, provider, and enums */
import { clearAuthData } from 'Actions/auth';
import { clearLessonData } from 'Actions/lesson';
import { authenticatedStartup, clearAppData } from 'Actions/app';
import { PURGE } from 'redux-persist';
import React, { useEffect } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'Reducers';
import { CognitoUserEtc } from 'Types';
import type { UpdateAuthDataValues, ChangePasswordData, ForgotPasswordData } from 'Types';
import { AuthReducer, ConfirmSignUpPayload, SignUpPayload } from 'Types/AuthTypes';
import {
  getCurrentAuthToken,
  getUser,
  manuallyClearUser,
  clearAuthCache,
  AUTH_FORCE_LOGOUT_EVENT,
  dispatchForceLogout,
} from 'Utils/Auth';
import * as authActions from 'Actions/auth';
import { SigninRedirectArgs, OidcClient } from 'oidc-client-ts';
import * as amplify from 'Utils/Amplify';
import { AuthError, signOut } from 'aws-amplify/auth';
import { handleAuthErrors } from 'Utils/Forms';
import { awsRum } from 'Utils/AwsRum';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { isEmail, isPhoneNumber } from 'Components/Forms/SignUpForm';
import { isCapacitorNative } from 'Utils/Constants';
import { filterFalsy } from 'Utils';
import { has, pick } from 'lodash';
import { codeFromCountryAcronym } from 'Components/Forms/CountryCodeSelect';
import CapacitorStorage, { CapacitorCache } from 'Utils/CapacitorStorage';
import { Browser } from '@capacitor/browser';

export enum ScopesEnum {
  'UNITS_READ' = 'museflow:units:read',
  'UNITS_WRITE' = 'museflow:units:write',
  'TUTORIALS_READ' = 'museflow:tutorials:read',
  'TUTORIALS_WRITE' = 'museflow:tutorials:write',
  'LEVELS_READ' = 'museflow:levels:read',
  'LEVELS_WRITE' = 'museflow:levels:write',
  'PHRASES_WRITE' = 'museflow:phrases:write',
  'PHRASES_READ' = 'museflow:phrases:read',
  'REPERTOIRES_READ' = 'museflow:repertoires:read',
  'REPERTOIRES_WRITE' = 'museflow:repertoires:write',
  /* eslint-disable @typescript-eslint/no-duplicate-enum-values -- admin scopes alias same OAuth scope string */
  'REPERTOIRES_ADMIN_READ' = 'museflow:repertoires:read',
  'REPERTOIRES_ADMIN_WRITE' = 'museflow:repertoires:write',
  /* eslint-enable @typescript-eslint/no-duplicate-enum-values */
  'USER_LEVEL_PROGRESS_READ' = 'museflow:user_level_progress:read',
  'USER_LEVEL_PROGRESS_WRITE' = 'museflow:user_level_progress:write',
  'USERDATA_READ' = 'museflow:userdata:read',
  'USERDATA_WRITE' = 'museflow:userdata:write',
  'USERDATA_ADMIN_READ' = 'museflow:userdata:admin:read',
  'USERDATA_ADMIN_WRITE' = 'museflow:userdata:admin:write',
  'USER_REPERTOIRE_PROGRESS_WRITE' = 'museflow:user_repertoire_progress:write',
  'EVENTS_WRITE' = 'museflow:events:write',
  'EVENTS_READ' = 'museflow:events:read',
  'EVENTS_ADMIN_WRITE' = 'museflow:events:admin:write',
  'EVENTS_ADMIN_READ' = 'museflow:events:admin:read',
  'NOTIFICATIONS_READ' = 'museflow:notifications:read',
  'NOTIFICATIONS_WRITE' = 'museflow:notifications:write',
  'PLAY_SESSION_WRITE' = 'museflow:playsession:write',
  'PLAY_SESSION_READ' = 'museflow:playsession:read',
  'PLAY_SESSION_ADMIN_WRITE' = 'museflow:playsession:admin:write',
  'PLAY_SESSION_ADMIN_READ' = 'museflow:playsession:admin:read',
  'STRIPE_ADMIN_WRITE' = 'museflow:stripe:admin:write',
  'STRIPE_ADMIN_READ' = 'museflow:stripe:admin:read',
  'METRICS_READ' = 'museflow:metrics:read',
}

export const userScopes = [
  ScopesEnum.USER_LEVEL_PROGRESS_WRITE,
  ScopesEnum.USER_LEVEL_PROGRESS_READ,
  ScopesEnum.PHRASES_READ,
  ScopesEnum.LEVELS_READ,
  ScopesEnum.TUTORIALS_READ,
  ScopesEnum.UNITS_READ,
  ScopesEnum.USERDATA_WRITE,
  ScopesEnum.USER_REPERTOIRE_PROGRESS_WRITE,
  ScopesEnum.REPERTOIRES_READ,
  ScopesEnum.REPERTOIRES_WRITE,
  ScopesEnum.EVENTS_WRITE,
  ScopesEnum.NOTIFICATIONS_READ,
  ScopesEnum.NOTIFICATIONS_WRITE,
  ScopesEnum.PLAY_SESSION_WRITE,
  ScopesEnum.METRICS_READ,
];

export const oidcCache = new CapacitorCache();

export const oidcManager = new UserManager({
  authority: `https://cognito-idp.us-west-2.amazonaws.com/${process.env.REACT_APP_COGNITO_USER_POOL_ID}`,
  client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
  redirect_uri: isCapacitorNative() ? 'museflow://auth-callback' : `${window.location.origin}/`,
  response_type: 'code',
  scope:
    'openid email profile ' +
    userScopes.map((scope) => `${process.env.REACT_APP_OAUTH_REDIRECT_URL}/${scope}`).join(' '),
  // ChatGPT is OBSESSED with this "offline_access" but I have no idea where to look or why that would be right (it's not present with the other scopes, we already have all checked already.)
  // scope: 'aws.cognito.signin.user.admin phone openid profile email offline_access',
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: oidcCache }),
});

// SetErrorsPayload contains all types of form fields for any auth related form
export interface SetErrorsPayload {
  code: string;
  emailOrPhone: string;
  name: string;
  password: string;
  repeatPassword: string;
  unknown: string;
  currentPassword: string;
  newPassword: string;
  repeatNewPassword: string;
}

const defaultErrors = {
  code: '',
  emailOrPhone: '',
  name: '',
  password: '',
  repeatPassword: '',
  unknown: '',
  currentPassword: '',
  newPassword: '',
  repeatNewPassword: '',
};

const AUTH_BOOTSTRAP_TIMEOUT_MS = 5000;

async function getUserForAuthBootstrap(label: string): Promise<CognitoUserEtc | undefined> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(
        `[Auth] ${label} timed out after ${AUTH_BOOTSTRAP_TIMEOUT_MS}ms; continuing unauthenticated.`,
      );
      resolve(undefined);
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);
  });

  try {
    return await Promise.race([getUser(), timeout]);
  } catch (err) {
    console.error(`[Auth] ${label} failed; continuing unauthenticated.`, err);
    awsRum?.recordError(err);
    return undefined;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export enum SIGN_IN_COMPONENT {
  SIGNIN = 'SIGNIN',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  VERIFICATION_CODE = 'VERIFICATION_CODE',
}

// SetErrorsPayload contains all types of form fields for any auth related form
export interface SetErrorsPayload {
  code: string;
  emailOrPhone: string;
  name: string;
  password: string;
  repeatPassword: string;
  unknown: string;
  currentPassword: string;
  newPassword: string;
  repeatNewPassword: string;
}

export interface AuthState {
  // jwtToken: string | undefined,
  // idToken: string | undefined,
  // user: AugmentedCognitoUser | undefined,
  user: Partial<CognitoUserEtc> | undefined;
  confirmationCode: string | undefined;
  usernameAuthPending: boolean;
  processPending: boolean;
  errors: SetErrorsPayload;
  resetEmailOrPhone: string | undefined;
  resetCountryAcronym: string | undefined;
  pendingEmailOrPhone: string | undefined;
  pendingCountryAcronym: string | undefined;
  pendingEmailOrPhoneSuccessful: boolean;
  signInComponent: SIGN_IN_COMPONENT;
  successMessage: string | undefined;
  failureMessage: string | undefined;
  isAuthenticated: boolean;
  authInitialized: boolean;
  isLoading?: boolean;
  oauthPending?: boolean;
  tempSignUpPass: string | undefined;
}

type ContextFunctions = {
  currentAuthToken: () => Promise<string | undefined>;
  user?: Partial<CognitoUserEtc>;
  logOut: () => void;
  signInRedirect: (args?: SigninRedirectArgs) => Promise<void>;
  logInUsername: (emailOrPhone: string, password: string, countryAcronym: string) => void;
  refreshUser: () => Promise<void>;
  verifyAttributes: () => Promise<void>;
  verifyAttributesSubmit: (code: string) => Promise<void>;
  updateAuthDataCognito: (updateAuthData: { payload: UpdateAuthDataValues }) => Promise<void>;
  changePassword: (changePassword: { payload: ChangePasswordData }) => Promise<void>;
  forgotPassword: (forgotPassword: { payload: ForgotPasswordData }) => Promise<void>;
  submitNewPassword: (submitNewPassword: { payload: { password: string } }) => Promise<void>;
  verifyPendingEmailOrPhone: (code: string) => Promise<void>;
  resendSignUpUsername: (emailOrPhone: string) => Promise<void>;
  confirmSignUpUsername: (evt: ConfirmSignUpPayload) => Promise<void>;
  signUpUsername: (evt: SignUpPayload) => Promise<void>;
  logInOidc: (provider: 'Google' | 'SignInWithApple') => Promise<void>;
  resetErrors: () => void;
  setConfirmationCode: (code: string | undefined) => void;
  setSignInComponent: (component: SIGN_IN_COMPONENT) => void;
  setPendingEmailOrPhone?: (val: string | undefined) => void;
  isOidcUser?: boolean;
};

type AuthContextState = ContextFunctions & AuthState;

export const AuthWrapperContext = React.createContext<AuthContextState | null>(null);

export const useAuthWrapperContext = () => {
  const context = React.useContext(AuthWrapperContext);
  if (!context) {
    throw new Error('useAuthWrapper must be used within an AuthWrapperProvider');
  }
  return context;
};

// This component is used to unify the functionality between the react-oidc-context
// and the Amplify based Redux auth state. Once we get away from the amplify library completely,
// potentially by using a lower level cognito lib, we can replace the amplify auth functions here
// with functions from that library.
const AuthWrapperProvider = ({ children }: { children: React.ReactNode }) => {
  const oidcAuth = useAuth();
  const reduxAuth = useSelector((state: AuthReducer) => state.authReducer);
  // dispatch used to store some state in redux that needs to be kept in local storage (temp pass for verification, isAuthenticated default)
  const dispatch = useDispatch<AppDispatch>();
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(
    reduxAuth.isAuthenticated || oidcAuth.isAuthenticated || false,
  );
  const [user, setUser] = React.useState<Partial<CognitoUserEtc>>();
  const [confirmationCode, setConfirmationCode] = React.useState<string | undefined>(undefined);
  const [usernameAuthPending, setUsernameAuthPending] = React.useState<boolean>(false);
  const [processPending, setProcessPending] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<SetErrorsPayload>(defaultErrors);
  const [resetEmailOrPhone, setResetEmailOrPhone] = React.useState<string | undefined>(undefined);
  const [resetCountryAcronym, setResetCountryAcronym] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingEmailOrPhone, setPendingEmailOrPhone] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingCountryAcronym, setPendingCountryAcronym] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingEmailOrPhoneSuccessful, _setPendingEmailOrPhoneSuccessful] =
    React.useState<boolean>(false);
  const [signInComponent, setSignInComponent] = React.useState<SIGN_IN_COMPONENT>(
    SIGN_IN_COMPONENT.SIGNIN,
  );
  const [successMessage, setSuccessMessage] = React.useState<string | undefined>(undefined);
  const [failureMessage, setFailureMessage] = React.useState<string | undefined>(undefined);
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [isOidcUser, setIsOidcUser] = React.useState<boolean>(false);

  useEffect(() => {
    dispatch(authActions.setIsLoading(reduxAuth.isLoading || false));
  }, [reduxAuth.isLoading]);

  useEffect(() => {
    //initially set user on load
    let cancelled = false;
    getUserForAuthBootstrap('initial auth lookup')
      .then((user) => {
        if (cancelled) return;
        if (user) {
          setUser(user);
          setIsAuthenticated(true);
        } else if (!reduxAuth.isAuthenticated) {
          // No persisted session — treat the missing user as logged out.
          setUser(undefined);
          setIsAuthenticated(false);
        }
        // else: redux-persist says we were authenticated but the lookup
        // transiently failed (common on native cold-start, where Amplify's
        // token read races the app relaunch). Stay optimistic instead of
        // bouncing to /signin — the 401 / invalid_grant force-logout path
        // signs out if the session is genuinely gone. See MUS-248.
      })
      .finally(() => {
        if (cancelled) return;
        dispatch(authActions.setIsLoading(false));
        // dispatch(authActions.setOauthPending(false));
        setInitialized(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);
  const resetErrors = React.useCallback(() => {
    setErrors(defaultErrors);
    setSuccessMessage(undefined);
    setFailureMessage(undefined);
  }, []);

  const logOut = React.useCallback(async () => {
    // Clear the auth token cache FIRST to prevent stale tokens being used
    clearAuthCache();

    dispatch(clearAuthData());
    dispatch(clearLessonData());
    dispatch(clearAppData());
    // Purge all redux-persist storage keys to ensure clean state for next user
    dispatch({ type: PURGE, key: 'authPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'mainAppPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'repertoirePersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'lessonPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'tutorialPersistance', result: () => {} });
    await CapacitorStorage.clear();
    setUser(undefined);
    setConfirmationCode(undefined);
    setUsernameAuthPending(false);
    setProcessPending(false);
    setErrors(defaultErrors);
    setSuccessMessage(undefined);
    setFailureMessage(undefined);
    setIsOidcUser(false);
    try {
      // Clear both the React hook state AND the underlying UserManager
      await oidcManager.removeUser();
      oidcAuth.removeUser();
      oidcAuth.signoutSilent();
    } catch {
      console.log('log out oidc did not work');
    }
    try {
      signOut();
    } catch (err) {
      const authError = err as AuthError;
      const handledAuthErrors = handleAuthErrors(authError);
      setErrors(handledAuthErrors);
      if (Object.keys(handledAuthErrors).includes('unknown')) {
        awsRum?.recordError(err);
      }
    }
    try {
      await manuallyClearUser();
    } catch {
      // intentionally empty
    }
    dispatch(authActions.setIsLoading(false));
    setIsAuthenticated(false);
  }, [oidcAuth.removeUser]);

  const logInUsername = React.useCallback(
    async (emailOrPhone: string, password: string, countryAcronym: string) => {
      try {
        dispatch(authActions.setIsLoading(true));
        // Clear any stale OIDC state before username/password login
        // This ensures getUser() fetches from Amplify, not stale OIDC cache
        clearAuthCache();
        await oidcManager.removeUser();
        await manuallyClearUser();

        try {
          await amplify.signIn({ emailOrPhone, password, countryAcronym });
          console.log(
            '[MUS-61] logInUsername: amplify.signIn succeeded, setting isAuthenticated=true',
          );
        } catch (signInError) {
          // On app relaunch (esp. iPad), routing can bounce a still-valid
          // session to /signin. Cognito then rejects a fresh sign-in with
          // "There is already a signed in user". That's not a real failure —
          // the existing session is valid, so recover it instead of surfacing
          // an "Unknown Error". See MUS-248.
          if ((signInError as AuthError)?.name === 'UserAlreadyAuthenticatedException') {
            console.warn(
              '[MUS-248] logInUsername: user already authenticated — recovering existing session',
            );
          } else {
            throw signInError;
          }
        }
        setIsAuthenticated(true);
        dispatch(authActions.setIsLoading(false));
        setPendingEmailOrPhone(undefined);
        console.log('[MUS-61] logInUsername: clearing tempSignUpPass');
        dispatch(authActions.setTempSignUpPass(undefined));
        localStorage.removeItem('tempSignUpPass');
        resetErrors();
        const user = await getUser();
        console.log('[MUS-61] logInUsername: got user, dispatching authenticatedStartup');
        setUser(user);
        dispatch(authenticatedStartup());
      } catch (error) {
        dispatch(authActions.setIsLoading(true));
        const authError = error as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(error as AuthError);
        console.error('Error logging in with username:', error);
        dispatch(authActions.setIsLoading(false));
        dispatch(authActions.setOauthPending(false));
        // as of amplify 6.0.0, the unverified error is now unauthenticated. Keeping "user not confirmed" just in case
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
          if (isPhoneNumber(emailOrPhone)) {
            setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
          }
        } else {
          setErrors(handledAuthErrors);
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(error);
          }
        }
      }
    },
    [],
  );

  const logInOidc = React.useCallback(async (identityProvider: 'Google' | 'SignInWithApple') => {
    dispatch(authActions.setOauthPending(true));
    dispatch(authActions.setIsLoading(true));
    try {
      if (isCapacitorNative()) {
        // iOS/Android native: open Hosted UI in SafariView (Apple-approved)
        const client = new OidcClient(oidcManager.settings);
        const req = await client.createSigninRequest({
          extraQueryParams: { identity_provider: identityProvider },
        });
        await Browser.open({ url: req.url }); // opens SFSafariViewController on iOS
      } else {
        // Web: keeps existing redirect flow
        await oidcManager.signinRedirect({
          extraQueryParams: { identity_provider: identityProvider },
        });
      }
    } catch (e) {
      dispatch(authActions.setIsLoading(false));
      dispatch(authActions.setOauthPending(false));
      throw e;
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      dispatch(authActions.setIsLoading(false));
      dispatch(authActions.setOauthPending(false));
    }, 5000);
  }, []);

  const signUpUsername = React.useCallback(async (evt: SignUpPayload) => {
    try {
      setProcessPending(true);
      // need this to persist so that we can log in after verification
      // Also persist to localStorage so it survives iOS Safari page eviction
      // and is accessible from the verification tab (sessionStorage is per-tab)
      dispatch(authActions.setTempSignUpPass(evt.password));
      try {
        localStorage.setItem('tempSignUpPass', evt.password);
      } catch {
        // localStorage may be unavailable in some contexts
      }
      await amplify.signUpUser(evt);

      setPendingEmailOrPhone(evt.emailOrPhone);
      if (isPhoneNumber(evt.emailOrPhone)) {
        setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
      } else {
        setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
      }
    } catch (err) {
      const authError = err as AuthError;
      if (
        authError.name === 'UserNotConfirmedException' ||
        authError.name === 'UserUnAuthenticatedException'
      ) {
        authActions.setPendingEmailOrPhone(evt.emailOrPhone);
      } else {
        const handledAuthErrors = handleAuthErrors(err as AuthError);
        setErrors(handledAuthErrors);
        if (Object.keys(handledAuthErrors).includes('unknown')) {
          awsRum?.recordError(err);
        }
      }
    } finally {
      setProcessPending(false);
    }
  }, []);

  const confirmSignUpUsername = React.useCallback(
    async (evt: ConfirmSignUpPayload) => {
      const { phoneNumber, confirmationCode, countryAcronym } = evt;
      try {
        if (phoneNumber && confirmationCode && reduxAuth.tempSignUpPass) {
          setProcessPending(true);
          await amplify.confirmSignUp(phoneNumber, confirmationCode, countryAcronym);
          setPendingEmailOrPhone(undefined);
          setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
          await logInUsername(phoneNumber, reduxAuth.tempSignUpPass, countryAcronym);
        } else {
          console.warn(
            `No emailOrPhone or ConfirmationCode on confirm sign up: emailOrPhone: ${phoneNumber}, confirmatonCode: ${confirmationCode}`,
          );
          awsRum?.recordError(
            new Error(
              `No emailOrPhone or ConfirmationCode on confirm sign up: emailOrPhone: ${phoneNumber}, confirmatonCode: ${confirmationCode}`,
            ),
          );
        }
      } catch (err) {
        const authError = err as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(phoneNumber);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [reduxAuth.tempSignUpPass],
  );

  const resendSignUpUsername = React.useCallback(
    async (emailOrPhone: string) => {
      try {
        await amplify.resendSignUp(emailOrPhone, pendingCountryAcronym);
      } catch (err) {
        const authError = err as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingCountryAcronym],
  );

  const verifyPendingEmailOrPhone = React.useCallback(
    async (code: string) => {
      if (pendingEmailOrPhone) {
        await amplify.confirmSignUp(pendingEmailOrPhone, code);
      }
      setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
    },
    [pendingEmailOrPhone],
  );

  const refreshUser = React.useCallback(async () => {
    try {
      // getUser() is pull-through cached; clear first so profile updates are immediately reflected
      clearAuthCache();
      const user = await getUser();

      setUser(user);
    } catch (err) {
      console.error('failed to refresh user', err);
      // setUser(undefined);
    }
  }, []);

  const verifyAttributes = React.useCallback(async () => {
    if (isPhoneNumber(pendingEmailOrPhone)) {
      await amplify.verifyUserAttribute('phone_number');
    }
    if (isEmail(pendingEmailOrPhone)) {
      await amplify.verifyUserAttribute('email');
    }
    setPendingEmailOrPhone(undefined);
    setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
  }, [pendingEmailOrPhone]);

  const verifyAttributesSubmit = React.useCallback(
    async (code: string) => {
      try {
        if (isPhoneNumber(pendingEmailOrPhone)) {
          await amplify.verifyCurrentUserAttributeSubmit('phone_number', code);
        }
        if (isEmail(pendingEmailOrPhone)) {
          await amplify.verifyCurrentUserAttributeSubmit('email', code);
        }
        await refreshUser();
        setPendingEmailOrPhone(undefined);
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          (authError.name === 'UserNotConfirmedException' ||
            authError.name === 'UserUnAuthenticatedException') &&
          !pendingEmailOrPhone
        ) {
          if (user?.attributes?.email) {
            setPendingEmailOrPhone(user?.attributes.email);
          } else if (user?.attributes?.phone_number) {
            setPendingEmailOrPhone(user?.attributes.phone_number);
          }
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingEmailOrPhone, user, refreshUser],
  );

  const updateAuthDataCognito = React.useCallback(
    async (updateAuthData: { payload: UpdateAuthDataValues }) => {
      const values = updateAuthData.payload;
      const filteredValues = filterFalsy(pick(updateAuthData.payload, ['name', 'emailOrPhone']));
      const finalValues: { name?: string; email?: string; phone_number?: string } = {};
      setProcessPending(true);
      if (has(filteredValues, 'emailOrPhone') && isPhoneNumber(filteredValues['emailOrPhone'])) {
        const strippedNumber =
          '+' +
          codeFromCountryAcronym(updateAuthData.payload.countryAcronym) +
          String(filteredValues['emailOrPhone']).replace(/[^0-9.]/g, '');
        finalValues['phone_number'] = strippedNumber;
      }
      if (has(filteredValues, 'emailOrPhone') && isEmail(filteredValues['emailOrPhone'])) {
        finalValues['email'] = filteredValues['emailOrPhone'];
      }
      if (has(filteredValues, 'name')) {
        finalValues.name = filteredValues.name;
      }
      try {
        await amplify.updateUserAttributes(finalValues);
        await refreshUser();
        if (
          values?.emailOrPhone &&
          user?.attributes?.email !== values?.emailOrPhone &&
          user?.attributes?.phone_number !== values?.emailOrPhone
        ) {
          setPendingEmailOrPhone(values.emailOrPhone);
          setPendingCountryAcronym(values.countryAcronym);
        } else {
          setSuccessMessage('profile successfully updated');
        }
        setProcessPending(false);
      } catch (err) {
        setProcessPending(false);
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(values.emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [user, refreshUser],
  );

  const changePassword = React.useCallback(
    async (changePassword: { payload: ChangePasswordData }) => {
      const { currentPassword, newPassword } = changePassword.payload;
      try {
        await amplify.changePassword(currentPassword, newPassword);
        setSuccessMessage('Password successfully updated');
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          (authError.name === 'UserNotConfirmedException' ||
            authError.name === 'UserUnAuthenticatedException') &&
          !pendingEmailOrPhone
        ) {
          if (user?.attributes?.email) {
            setPendingEmailOrPhone(user?.attributes.email);
          } else if (user?.attributes?.phone_number) {
            setPendingEmailOrPhone(user?.attributes.phone_number);
          }
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingEmailOrPhone, user],
  );

  const forgotPassword = React.useCallback(
    async (forgotPassword: { payload: ForgotPasswordData }) => {
      const { emailOrPhone, countryAcronym } = forgotPassword.payload;
      try {
        await amplify.forgotPassword(emailOrPhone, countryAcronym);
        setResetEmailOrPhone(emailOrPhone);
        setResetCountryAcronym(countryAcronym);
        setProcessPending(false);
        setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          // setAuthPending(true) -- you may want to add this state if needed
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [],
  );

  const submitNewPassword = React.useCallback(
    async (submitNewPassword: { payload: { password: string } }) => {
      const { password } = submitNewPassword.payload;
      const emailOrPhone =
        resetEmailOrPhone || user?.attributes?.email || user?.attributes?.phone_number;
      try {
        if (confirmationCode && emailOrPhone) {
          await amplify.submitForgotPassword(
            emailOrPhone,
            confirmationCode,
            password,
            resetCountryAcronym,
          );
          setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
        } else {
          console.warn(
            `No emailOrPhone or ConfirmationCode on submit new password: emailOrPhone: ${emailOrPhone}, confirmatonCode: ${confirmationCode}`,
          );
          awsRum?.recordError(
            new Error(
              `No emailOrPhone or ConfirmationCode on submit new password: emailOrPhone: ${emailOrPhone}, confirmatonCode: ${confirmationCode}`,
            ),
          );
        }
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          // setAuthPending(true) -- you may want to add this state if needed
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [resetEmailOrPhone, user, confirmationCode, resetCountryAcronym],
  );

  // propagate oidc context changes to the auth wrapper context
  React.useEffect(() => {
    console.log('oidcAuth.isAuthenticated changed', oidcAuth.isAuthenticated);

    if (oidcAuth.isLoading) {
      dispatch(authActions.setIsLoading(true));
      return;
    }

    if (oidcAuth.isAuthenticated) {
      setIsAuthenticated(true);
    } else if (isOidcUser) {
      setIsAuthenticated(false);
      setUser(undefined);
      setIsOidcUser(false);
    }

    dispatch(authActions.setIsLoading(false));

    // Clear OAuth params from URL after successful authentication
    if (oidcAuth.isAuthenticated && window.location.search) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.pathname);
    }
  }, [dispatch, isOidcUser, oidcAuth.isAuthenticated, oidcAuth.isLoading]);

  React.useEffect(() => {
    dispatch(authActions.setIsAuthenticated(isAuthenticated));
    dispatch(authActions.setIsLoading(false));
  }, [isAuthenticated]);

  React.useEffect(() => {
    console.log('oidcAuth.user changed', oidcAuth.user);
    if (oidcAuth?.user) {
      setIsOidcUser(true);
      setUser({
        signInUserSession: {
          accessToken: {
            jwtToken: oidcAuth.user.access_token || '',
          },
        },
        username: oidcAuth.user.profile.email,
        attributes: {
          email: oidcAuth.user.profile.email,
          name: oidcAuth.user.profile.name,
          sub: oidcAuth.user.profile.sub,
          phone_number: oidcAuth.user.profile.phone_number,
        },
      });
      dispatch(authActions.setOauthPending(false));
      dispatch(authenticatedStartup());
    }
    if (oidcAuth?.error) {
      dispatch(authActions.setOauthPending(false));
      dispatch(authActions.setIsLoading(false));
    }
  }, [oidcAuth.user, oidcAuth.error]);

  React.useEffect(() => {
    let cancelled = false;

    getUserForAuthBootstrap('auth refresh on mount')
      .then((user) => {
        if (cancelled) return;
        if (user) {
          setUser(user);
          setIsAuthenticated(true);
        } else if (!reduxAuth.isAuthenticated) {
          // No persisted session — treat the missing user as logged out.
          setUser(undefined);
          setIsAuthenticated(false);
        }
        // else: stay optimistic on a transient lookup miss with a persisted
        // session; force-logout handles a truly expired session. See MUS-248.
      })
      .finally(() => {
        if (cancelled) return;
        dispatch(authActions.setIsLoading(false));
      });

    // Handle access token expiring - attempt silent refresh
    oidcManager.events.addAccessTokenExpiring(async () => {
      try {
        await oidcManager.signinSilent();
        console.log('[OIDC] access-token renewed');
      } catch (err: unknown) {
        console.error('[OIDC] silent renew failed', err);

        // Check for invalid_grant or 401 error - this means the refresh token is expired/revoked
        const errObj = err as {
          message?: string;
          error?: string;
          status?: number;
          response?: { status?: number };
        };
        const errorMessage = errObj?.message ?? errObj?.error ?? String(err);
        const status = errObj?.status ?? errObj?.response?.status;
        const isInvalidGrant =
          errorMessage.includes('invalid_grant') || errObj?.error === 'invalid_grant';
        const is401 = status === 401 || errorMessage.includes('401');

        if (isInvalidGrant || is401) {
          console.error(
            `[OIDC] Auth error (invalid_grant: ${isInvalidGrant}, 401: ${is401}) - forcing logout`,
          );
          dispatchForceLogout(`Token renewal failed: ${isInvalidGrant ? 'invalid_grant' : '401'}`);
        } else {
          setIsAuthenticated(false);
          setUser(undefined);
        }
      }
    });

    // Listen for force logout events (triggered when token refresh fails with invalid_grant)
    const handleForceLogout = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.warn('[Auth] Force logout triggered:', customEvent.detail?.reason);
      await logOut();
      // Navigate to sign-in page
      window.location.href = '/signin';
    };
    window.addEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);

    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_FORCE_LOGOUT_EVENT, handleForceLogout);
    };
  }, [logOut]);

  return (
    <AuthWrapperContext.Provider
      value={{
        isOidcUser,
        isAuthenticated,
        currentAuthToken: getCurrentAuthToken,
        user,
        logOut,
        signInRedirect: oidcAuth.signinRedirect,
        logInUsername: logInUsername,
        setConfirmationCode,
        confirmationCode,
        usernameAuthPending,
        processPending,
        errors,
        resetEmailOrPhone,
        resetCountryAcronym,
        pendingEmailOrPhone,
        pendingCountryAcronym,
        pendingEmailOrPhoneSuccessful,
        signInComponent,
        successMessage,
        setSignInComponent,
        failureMessage,
        refreshUser,
        verifyAttributes,
        verifyAttributesSubmit,
        updateAuthDataCognito,
        submitNewPassword,
        forgotPassword,
        changePassword,
        verifyPendingEmailOrPhone,
        resendSignUpUsername,
        confirmSignUpUsername,
        signUpUsername,
        logInOidc,
        resetErrors,
        setPendingEmailOrPhone,
        authInitialized: initialized,
        isLoading: Boolean(reduxAuth.isLoading || oidcAuth.isLoading || !initialized),
        oauthPending: reduxAuth.oauthPending,
        tempSignUpPass: reduxAuth.tempSignUpPass,
      }}
    >
      {children}
    </AuthWrapperContext.Provider>
  );
};

function AuthWrapperExport(props: { children: React.ReactNode }) {
  return (
    <AuthProvider userManager={oidcManager}>
      <AuthWrapperProvider>{props.children}</AuthWrapperProvider>
    </AuthProvider>
  );
}
export default AuthWrapperExport;
